import { useId } from 'react';
import { Select, Space, Typography } from 'antd';
import { space } from '@viq-admin/ui';
import { crmSourceLabel } from '../../integrations/crmSourceLabel';
import { CRM_COPY } from './copy';

export interface CrmSourceControlProps {
  /** Every CRM this company has connected, from `CrmQueueApi.connectedSources`. */
  sources: string[];
  /** The source the whole queue — Clean, Match and Send alike — is scoped to. */
  value: string;
  canManage: boolean;
  /**
   * Requests a switch. Deliberately NOT `onChange`: the owner (CrmViews) may
   * confirm first, and may decline to change anything at all. This component
   * never assumes its request was honoured — `value` is the only truth.
   */
  onRequestChange: (next: string) => void;
}

/**
 * The stage strip's CRM scope indicator (and, with more than one source
 * connected, its picker). Presentation only: holds no state, knows nothing
 * about the queue, and never dispatches.
 *
 * Read-only text rather than a disabled dropdown when there is nothing to
 * choose — a disabled control invites clicking and explains nothing — but the
 * scope is still stated, because "which CRM am I looking at?" is a fair
 * question for a single-CRM company too.
 */
export function CrmSourceControl({
  sources,
  value,
  canManage,
  onRequestChange,
}: CrmSourceControlProps) {
  // Per-instance, not a fixed string: a literal id would collide if this ever
  // rendered twice on one page. `useId`'s raw value contains colons (`:r3:`)
  // — a legal `id`, but not valid CSS selector syntax, and the accessible-
  // name computation both jsdom's test environment and some real browsers
  // run walks up through `querySelector` calls that choke on it (same
  // reasoning as `HelpHint.tsx`'s `useDomId`) — so it's stripped down to
  // plain alphanumerics before use.
  const labelId = `crm-source-label-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;

  // Defensive, not decorative: the parent gates on `connectedSources.length > 0`,
  // and this keeps the control from rendering a labelled empty box if that
  // gate ever moves.
  if (sources.length === 0) return null;

  if (sources.length === 1 || !canManage) {
    return (
      <Typography.Text type="secondary" data-testid="crm-source-readonly">
        {CRM_COPY.sourceControlReadOnly(crmSourceLabel(value))}
      </Typography.Text>
    );
  }

  return (
    <Space size={space.xs} align="center" data-testid="crm-source-control">
      {/* The Select's accessible name comes from THIS text via
          `aria-labelledby`, not a separate `aria-label` — a screen reader
          announcing the visible "CRM" and an `aria-label` echo of the same
          word back to back was the defect (`aria-labelledby` wins over any
          `aria-label` were both present, but carrying both invites exactly
          that drift the next time either one is edited alone). */}
      <Typography.Text type="secondary" id={labelId}>
        {CRM_COPY.sourceControlLabel}
      </Typography.Text>
      <Select
        aria-labelledby={labelId}
        size="small"
        style={{ minWidth: 140 }}
        value={value}
        // `value` stays the raw source id — everything downstream (the
        // reducer's `setSource`, the queue keys, the eventual API calls)
        // keys off that id. Only `label` — what the closed control and the
        // open list actually paint — goes through `crmSourceLabel`, so an
        // operator sees "Salesforce"/"HubSpot" rather than the wire id.
        options={sources.map((s) => ({ value: s, label: crmSourceLabel(s) }))}
        onChange={(next: string) => onRequestChange(next)}
      />
    </Space>
  );
}
